import { DbRef, DUMMY_PLACE_GROUP_ID, SamplesDeliveryType, orderedMedicalTitleRanking } from '@caresend/types';
import { getUnknownErrorMessage } from '../error/errorHandling.js';
import { timeInMsToHoursAndMinutes } from '../formatters/date.js';
import { HOUR } from '../initializers/date.js';
import { deduplicateArray } from '../languageExtensions/array.js';
import { objectFilter, objectSome } from '../languageExtensions/object.js';
import { nullishFilter, isNullish } from '../typeValidation/nullish.js';

const getTimeSlotPath = (timeSlot, childPath) => `${DbRef.TIME_SLOTS_BY_SERVICE_REGION}/${timeSlot.serviceRegionID}` + `/${timeSlot.startDate}/${timeSlot.startTime}` + (childPath ? `/${childPath}` : '');

const getAllowedMedicalTitles = minimumTitle => {
  const titleIndex = orderedMedicalTitleRanking.indexOf(minimumTitle);
  return orderedMedicalTitleRanking.slice(undefined, titleIndex + 1);
};

const doesNurseMeetOfficeAffiliation = (nurse, draftWaypoint) => {
  const {
    officeID,
    createdByOfficeID
  } = draftWaypoint;
  const {
    allOfficeAffiliations,
    officeAffiliations
  } = nurse;
  if (allOfficeAffiliations) return true;
  if (officeID && (officeAffiliations === null || officeAffiliations === void 0 ? void 0 : officeAffiliations[officeID])) return true;
  if (createdByOfficeID && (officeAffiliations === null || officeAffiliations === void 0 ? void 0 : officeAffiliations[createdByOfficeID])) return true;
  return false;
};
/**
 * Returns true if the nurse meets the offer deadline. If the offer deadline
 * is in the past, the nurse is not eligible to fill the visit.
 */


const doesNurseMeetOfferDeadline = (nurse, timeSlotStartTime) => {
  const {
    offerDeadline
  } = nurse;
  if (offerDeadline === 0) return true;
  return timeSlotStartTime - Date.now() >= offerDeadline;
};
/**
 * Returns true if the nurse meets the following criteria:
 * - Active
 * - Owns a centrifuge if required
 * - Is not a pending nurse if not allowed
 * - Has the required medical title
 * - Is affiliated with the required offices
 * - Meets the offer deadline
 */


const isValidNurse = (nurse, draftWaypoint, timeSlotStartTime, requiresCentrifuge, usePendingNurseID, allowedTitles) => {
  const {
    active,
    ownsCentrifuge,
    nurseID,
    medicalTitle
  } = nurse;
  if (!active) return false;
  const missingCentrifuge = requiresCentrifuge && !ownsCentrifuge;
  if (missingCentrifuge) return false;
  const isDisallowedPendingNurse = !usePendingNurseID && nurseID === 'pendingNurseID';
  if (isDisallowedPendingNurse) return false;
  const isAllowedMedicalTitle = allowedTitles.includes(medicalTitle);
  if (!isAllowedMedicalTitle) return false;
  const meetsOfficeAffiliation = doesNurseMeetOfficeAffiliation(nurse, draftWaypoint);
  if (!meetsOfficeAffiliation) return false;
  const meetsOfferDeadline = doesNurseMeetOfferDeadline(nurse, timeSlotStartTime);
  if (!meetsOfferDeadline) return false;
  return true;
};

const getIsMailIn = (timeSlot, visitPlaceGroupID) => {
  var _a, _b, _c;

  return !((_c = (_b = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.walkIn) === null || _c === void 0 ? void 0 : _c.isOpen);
};
/**
 * Get the place group IDs associated with the lab drop-off on the
 * procedures found on this visit. For draft orders, it may be possible that the
 * lab is not yet selected. For draft orders that are ready for scheduling, a
 * lab is always expected to have been selected.
 */

const getVisitPlaceGroupIDs = params => params.draftWaypoint.draftOrderID ? [DUMMY_PLACE_GROUP_ID] : deduplicateArray(Object.values(params.procedures).map(procedure => {
  var _a;

  return Object.keys((_a = procedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {});
}).flat());
/**
 * Get the non-draft procedure IDs that require mail-in for a given place group.
 */

const getNonDraftMailInProcedureIDs = (params, visitPlaceGroupID) => {
  const {
    draftWaypoint,
    procedures,
    timeSlot
  } = params;
  if (draftWaypoint.draftOrderID) return [];
  return procedures.filter(procedure => procedure.id && procedure.dropOffPlaceGroups && procedure.dropOffPlaceGroups[visitPlaceGroupID] && getIsMailIn(timeSlot, visitPlaceGroupID)).map(procedure => procedure.id);
};
/**
 * Assumes time slot has already been checked and walk-in is open. This
 * only checks non-draft procedures, as draft procedures do not have supply
 * config options.
 */


const getNonDraftProceduresWalkInConfigFailure = (params, visitPlaceGroupID) => {
  const {
    options
  } = params;
  const failures = params.procedures.map(procedure => {
    var _a, _b, _c;

    const procedureID = procedure.id;
    const procedureOptions = options[procedureID];
    const walkInConfigExists = !!((_c = (_b = (_a = procedureOptions === null || procedureOptions === void 0 ? void 0 : procedureOptions.supplyInstructionsByPlaceGroup) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.supplyInstructionsByDeliveryType) === null || _c === void 0 ? void 0 : _c.walkIn);

    if (!walkInConfigExists) {
      return `No walk-in supply config for procedure ${procedureID} ` + `matching place group ${visitPlaceGroupID}`;
    }

    return null;
  });
  if (failures.find(nullishFilter)) return null;
  if (failures.filter(nullishFilter).length) return failures.join(', ');
  return null;
};
/**
 * Returns lowest sample stability on the mail-in option for all of the
 * (non-draft) mail-inprocedures.
 */


const getLowestMailInSampleStability = (params, visitPlaceGroupID) => {
  const {
    options
  } = params;
  const isMailIn = getIsMailIn(params.timeSlot, visitPlaceGroupID);
  if (!isMailIn) return null;
  const nonDraftMailInProcedureIDs = getNonDraftMailInProcedureIDs(params, visitPlaceGroupID);
  if (!nonDraftMailInProcedureIDs.length) return null;
  const sampleStabilities = nonDraftMailInProcedureIDs.map(procedureID => {
    var _a, _b, _c, _d, _e;

    const sampleStability = (_e = (_d = (_c = (_b = (_a = options[procedureID]) === null || _a === void 0 ? void 0 : _a.supplyInstructionsByPlaceGroup) === null || _b === void 0 ? void 0 : _b[visitPlaceGroupID]) === null || _c === void 0 ? void 0 : _c.supplyInstructionsByDeliveryType) === null || _d === void 0 ? void 0 : _d.mailIn) === null || _e === void 0 ? void 0 : _e.sampleStability;
    if (sampleStability) return sampleStability;
  }).filter(nullishFilter);
  if (!sampleStabilities.length) return null;
  return Math.min(...sampleStabilities);
};
/**
 * Based on time slot start, `estimatedAcceptanceTimestamp` and `sampleStability`,
 * determine if the time slot is compatible with mailing to the lab and being
 * accepted before samples expire.
 */


const getMailInFailure = (params, visitPlaceGroupID) => {
  try {
    const {
      labShipmentInfoByPlaceGroup,
      timeSlot
    } = params;
    const isMailIn = getIsMailIn(timeSlot, visitPlaceGroupID);
    if (!isMailIn) throw Error('No mail-in option on timeslot');
    const sampleStability = getLowestMailInSampleStability(params, visitPlaceGroupID);

    if (!sampleStability) {
      const nonDraftMailInProcedureIDs = getNonDraftMailInProcedureIDs(params, visitPlaceGroupID);
      throw Error('Missing mail-in sample stability data in supply instruction config ' + `options on at least one of the following procedures: ${nonDraftMailInProcedureIDs.join(', ')}`);
    }

    const labShipmentInfo = labShipmentInfoByPlaceGroup === null || labShipmentInfoByPlaceGroup === void 0 ? void 0 : labShipmentInfoByPlaceGroup[visitPlaceGroupID];
    if (!labShipmentInfo) throw Error('No lab shipment info found');
    const estimatedAcceptanceTimestamp = labShipmentInfo.estimatedAcceptanceTimestamp;
    if (isNullish(estimatedAcceptanceTimestamp)) throw Error('No estimatedAcceptanceTimestamp found');
    const timeSlotStart = timeSlot.startTime;
    const stabilityInMs = sampleStability * HOUR;
    const stabilityExpiry = timeSlotStart + stabilityInMs;
    const mailInWorks = estimatedAcceptanceTimestamp < stabilityExpiry;

    if (!mailInWorks) {
      const pastDeadlineMs = estimatedAcceptanceTimestamp - stabilityExpiry;
      const pastDeadline = timeInMsToHoursAndMinutes(pastDeadlineMs);
      throw Error(`Mail-in acceptance ${pastDeadline} too late.`);
    }

    return null;
  } catch (error) {
    return `Mail-in failure for place group ${visitPlaceGroupID}: ${getUnknownErrorMessage(error)}`;
  }
};
/**
 * For a given visit, we must ensure the associated place group is listed on
 * the time slot and is open (hours of operation).
 *
 * Returns true if:
 *   - Place group is included on the time slot and either `walkIn` or `mailIn`
 *     is open
 *   - For full orders, matching `walkIn` or `mailIn` is available on the
 *     supply instruction config options.
 *   - If only `mailIn` is open, given time slot `startTime`,
 *     `estimatedAcceptanceTimestamp` and `sampleStability`, the time slot must
 *     be compatible with mailing to the lab and being accepted before samples
 *     expire.
 */


const getPlaceGroupsFailure = (params, placeGroupID) => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = placeGroupID ? [placeGroupID] : getVisitPlaceGroupIDs(params);
  const failures = visitPlaceGroupIDs.map(visitPlaceGroupID => {
    var _a, _b, _c;

    const placeGroup = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID];
    if (!placeGroup) return `Place group ${visitPlaceGroupID} not available on time slot`;

    if ((_b = placeGroup.walkIn) === null || _b === void 0 ? void 0 : _b.isOpen) {
      return getNonDraftProceduresWalkInConfigFailure(params, visitPlaceGroupID);
    }

    if ((_c = placeGroup.mailIn) === null || _c === void 0 ? void 0 : _c.isOpen) {
      return getMailInFailure(params, visitPlaceGroupID);
    }

    return null;
  }).filter(nullishFilter);
  if (failures.length) return failures.join(', ');
  return null;
};

const getSamplesDeliveryType = params => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = getVisitPlaceGroupIDs(params);
  const placeGroupsFailure = getPlaceGroupsFailure(params);
  if (placeGroupsFailure) return null;
  const placeGroups = visitPlaceGroupIDs.map(visitPlaceGroupID => {
    var _a;

    const placeGroup = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID];
    return placeGroup;
  }).filter(nullishFilter);
  if (!placeGroups.length) return null;
  const somePlaceGroupIsMailIn = placeGroups.some(placeGroup => {
    var _a;

    return !((_a = placeGroup === null || placeGroup === void 0 ? void 0 : placeGroup.walkIn) === null || _a === void 0 ? void 0 : _a.isOpen);
  });
  return somePlaceGroupIsMailIn ? SamplesDeliveryType.MAIL_IN : SamplesDeliveryType.WALK_IN;
};
/**
 * Returns:
 * - `true` if at least one procedure requires centrifuge
 * - `false` if no procedures require centrifuge
 * - `null` if data is not available to determine if procedures will require
 *   centrifuge (i.e. on a draft order, or on an ineligible slot). If the slot
 *   is eligible, the visit will be treated as if no centrifuge is required.
 *
 * TODO: refactor to support multiple place groups
 */

const getRequiresCentrifugeWithoutTimeSlot = (samplesDeliveryType, params) => {
  const {
    options
  } = params;
  const optionsArray = Object.entries(options);
  if (!optionsArray.length) return null;
  let missingOptionSet = false;
  const optionsForVisitWithPlaceGroupID = optionsArray.map(([procedureID, supplyInstructions]) => {
    var _a, _b, _c;

    const procedurePlaceGroupID = (_c = (_b = (_a = params.procedures.find(procedure => procedure.id === procedureID)) === null || _a === void 0 ? void 0 : _a.dropOffPlaceGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
    if (!procedurePlaceGroupID) return null;
    const option = supplyInstructions.supplyInstructionsByPlaceGroup[procedurePlaceGroupID];

    if (!option) {
      missingOptionSet = true;
      return null;
    }

    return {
      option,
      procedurePlaceGroupID
    };
  }).filter(nullishFilter);
  if (missingOptionSet) return null;
  const requiresCentrifuge = optionsForVisitWithPlaceGroupID.some(optionSet => {
    var _a;

    const option = optionSet.option.supplyInstructionsByDeliveryType[samplesDeliveryType];
    return !!((_a = option === null || option === void 0 ? void 0 : option.processingIncludes) === null || _a === void 0 ? void 0 : _a.centrifuge);
  });
  return requiresCentrifuge;
};
/**
 * Returns:
 * - `true` if at least one procedure requires centrifuge
 * - `false` if no procedures require centrifuge
 * - `null` if data is not available to determine if procedures will require
 *   centrifuge (i.e. on a draft order, or on an ineligible slot). If the slot
 *   is eligible, the visit will be treated as if no centrifuge is required.
 *
 * TODO: refactor to support multiple place groups
 */

const getRequiresCentrifuge = params => {
  const {
    options
  } = params;
  const optionsArray = Object.entries(options);
  if (!optionsArray.length) return null;
  const samplesDeliveryType = getSamplesDeliveryType(params);
  if (!samplesDeliveryType) return null;
  let missingOptionSet = false;
  const optionsForVisit = optionsArray.map(([procedureID, supplyInstructions]) => {
    var _a, _b, _c;

    const procedurePlaceGroupID = (_c = (_b = (_a = params.procedures.find(procedure => procedure.id === procedureID)) === null || _a === void 0 ? void 0 : _a.dropOffPlaceGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
    if (!procedurePlaceGroupID) return null;
    const optionSet = supplyInstructions.supplyInstructionsByPlaceGroup[procedurePlaceGroupID];

    if (!optionSet) {
      missingOptionSet = true;
      return null;
    }

    return optionSet;
  }).filter(nullishFilter);
  if (missingOptionSet) return null;
  const requiresCentrifuge = optionsForVisit.some(optionSet => {
    var _a;

    const option = optionSet.supplyInstructionsByDeliveryType[samplesDeliveryType];
    return !!((_a = option === null || option === void 0 ? void 0 : option.processingIncludes) === null || _a === void 0 ? void 0 : _a.centrifuge);
  });
  return requiresCentrifuge;
};
/**
 * Check if a given nurse has the required supplies.
 */


const getNurseHasSupplies = (nurse, requiredSupplies) => {
  if (nurse.allSupplies) return true;
  if (!nurse.supplies) return false;
  const nurseSupplyIDs = Object.keys(nurse.supplies);
  const notAllSuppliesMet = requiredSupplies.some(supplyID => !nurseSupplyIDs.includes(supplyID));
  return !notAllSuppliesMet;
};
/**
 * Return all eligible nurses on a timeslot. The nurses must must meet the
 * following criteria:
 * - Active
 * - Owns a centrifuge if required
 * - Has the required medical title
 * - Is affiliated with the required offices
 * - Meets the offer deadline
 *
 * Note: In some cases we need to separately check if the nurse has all of the
 * required supplies. This is required if the
 * `SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND` filter is active, and we
 * expected a nurse to have all of the required supplies.
 */

const getEligibleNurses = (params, minimumTitle, usePendingNurseID) => {
  const {
    draftWaypoint,
    timeSlot
  } = params;
  const {
    availableNurses
  } = timeSlot;
  const requiresCentrifuge = getRequiresCentrifuge(params);
  const allowedTitles = getAllowedMedicalTitles(minimumTitle);
  const eligibleNurses = objectFilter(availableNurses, nurse => isValidNurse(nurse, draftWaypoint, timeSlot.startTime, requiresCentrifuge !== null && requiresCentrifuge !== void 0 ? requiresCentrifuge : false, usePendingNurseID !== null && usePendingNurseID !== void 0 ? usePendingNurseID : false, allowedTitles));
  return eligibleNurses;
};
/**
 * Updates the ranking on all nurse availabilities based on the labor cost.
 * The logic in here may change in the future.
 */

const getRankedNurseAvailabilities = allNurseAvailabilities => {
  const rankedNurseAvailabilities = {};
  const sortedAvailabilities = Object.values(allNurseAvailabilities).sort((a, b) => a.laborCost - b.laborCost);
  sortedAvailabilities.forEach((nurseAvailability, index) => {
    rankedNurseAvailabilities[nurseAvailability.nurseID] = Object.assign(Object.assign({}, nurseAvailability), {
      rank: index
    });
  });
  return rankedNurseAvailabilities;
};
/**
 * Gets the best eligible nurse (e.g. highest ranked) for a given time slot.
 * Only call this function once the nurses have been ranked on the timeslot.
 */

const getBestEligibleNurse = (eligibleNurses, preferredOfficeIDs) => {
  const eligibleNursesArray = Object.values(eligibleNurses);
  if (!eligibleNursesArray.length) return;
  const rankedNurses = eligibleNursesArray.sort((a, b) => a.rank - b.rank);
  if (!(preferredOfficeIDs === null || preferredOfficeIDs === void 0 ? void 0 : preferredOfficeIDs.length)) return rankedNurses[0];
  const bestNurse = rankedNurses.find(nurse => preferredOfficeIDs.includes(nurse.staffingOfficeID));
  return bestNurse;
};
const sortNursesByPreferredStaffingOffices = (availableNurses, preferredOffices) => Object.values(availableNurses).sort((a, b) => {
  const aIsPreferred = preferredOffices === null || preferredOffices === void 0 ? void 0 : preferredOffices.includes(a.staffingOfficeID);
  const bIsPreferred = preferredOffices === null || preferredOffices === void 0 ? void 0 : preferredOffices.includes(b.staffingOfficeID);
  if (aIsPreferred && !bIsPreferred) return -1;
  if (!aIsPreferred && bIsPreferred) return 1;
  return 0;
});
const getArrayOfAllTimeSlots = timeSlots => Object.values(timeSlots).flatMap(timeSlotsByDate => Object.values(timeSlotsByDate));
/**
 * Returns timeslots that are not disabled. This is useful during debug mode
 * when all timeslots get returned, but with the disabled flag set if
 * ineligible.
 */

const getEligibleTimeSlots = timeSlotsByDate => {
  const eligibleTimeSlotsByDate = {};
  Object.entries(timeSlotsByDate).forEach(([date, timeSlots]) => {
    Object.entries(timeSlots).forEach(([time, timeSlot]) => {
      if (!timeSlot.disabled) {
        eligibleTimeSlotsByDate[date] = Object.assign(Object.assign({}, eligibleTimeSlotsByDate[date]), {
          [time]: timeSlot
        });
      }
    });
  });
  return eligibleTimeSlotsByDate;
};
/**
 * Returns true if the nurse is from the integration name provided.
 * If no integration name is provided, returns true if the nurse is from any
 * integration.
 */

const isIntegrationNurse = (nurse, integrationName) => {
  var _a;

  const nurseIntegrationName = (_a = nurse.integration) === null || _a === void 0 ? void 0 : _a.name;
  return integrationName ? nurseIntegrationName === integrationName : !!nurseIntegrationName;
};
/**
 * Returns timeslots that have nurses from the integration. Useful for debugging
 */

const getTimeSlotsWithIntegrationNurses = (timeSlots, integrationName) => Object.values(timeSlots).reduce((acc, slotsByDate) => {
  Object.values(slotsByDate).forEach(timeSlot => {
    const {
      availableNurses
    } = timeSlot;
    const hasIntegrationNurse = objectSome(availableNurses, nurse => isIntegrationNurse(nurse, integrationName));

    if (hasIntegrationNurse) {
      acc[timeSlot.startDate] = Object.assign(Object.assign({}, acc[timeSlot.startDate]), {
        [timeSlot.startTime]: timeSlot
      });
    }
  });
  return acc;
}, {});

export { getArrayOfAllTimeSlots, getBestEligibleNurse, getEligibleNurses, getEligibleTimeSlots, getIsMailIn, getNurseHasSupplies, getRankedNurseAvailabilities, getRequiresCentrifugeWithoutTimeSlot, getSamplesDeliveryType, getTimeSlotPath, getTimeSlotsWithIntegrationNurses, getVisitPlaceGroupIDs, isIntegrationNurse, sortNursesByPreferredStaffingOffices };
